import { useState, useEffect } from "react";
import { Star, ChevronDown, MessageSquare, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { formatDistanceToNow } from "date-fns";
import { ptBR } from "date-fns/locale";
import { motion, AnimatePresence } from "framer-motion";

interface Review {
  id: string;
  food_rating: number;
  service_rating: number;
  delivery_rating: number | null;
  comment: string | null;
  tags: string[];
  customer_name: string | null;
  created_at: string;
}

interface PublicReviewsDialogProps {
  restaurantId: string;
  menuAccent: string;
  menuText: string;
  menuBg: string;
  open: boolean;
  onClose: () => void;
}

const anonymizeName = (name: string | null): string => {
  if (!name || name.trim().length === 0) return "Cliente";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
  const first = parts[0].charAt(0).toUpperCase() + parts[0].slice(1).toLowerCase();
  const lastInitial = parts[parts.length - 1].charAt(0).toUpperCase() + ".";
  return `${first} ${lastInitial}`;
};

const StarDisplay = ({ rating, size = 14 }: { rating: number; size?: number }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(i => (
      <Star
        key={i}
        className={i <= rating ? "fill-current" : ""}
        style={{ color: i <= rating ? "hsl(45, 93%, 47%)" : "hsl(0, 0%, 80%)", width: size, height: size }}
      />
    ))}
  </div>
);

export const PublicReviewsDialog = ({ restaurantId, menuAccent, menuText, menuBg, open, onClose }: PublicReviewsDialogProps) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!open) return;
    const load = async () => {
      setLoading(true);
      const { data } = await supabase
        .from("order_ratings")
        .select("id, food_rating, service_rating, delivery_rating, comment, tags, customer_name, created_at")
        .eq("restaurant_id", restaurantId)
        .gte("food_rating", 1)
        .order("created_at", { ascending: false })
        .limit(50);
      setReviews((data as any[]) || []);
      setLoading(false);
    };
    load();
  }, [restaurantId, open]);

  if (!open) return null;

  const filtered = filter
    ? reviews.filter(r => Math.round((r.food_rating + r.service_rating) / 2) >= filter)
    : reviews;

  const displayed = showAll ? filtered : filtered.slice(0, 8);

  const avgRating = reviews.length > 0
    ? reviews.reduce((s, r) => s + (r.food_rating + r.service_rating) / 2, 0) / reviews.length
    : 0;

  const ratingCounts = [5, 4, 3, 2, 1].map(n => ({
    stars: n,
    count: reviews.filter(r => Math.round((r.food_rating + r.service_rating) / 2) === n).length,
  }));

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
            onClick={onClose}
          />
          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.95 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-6 bottom-6 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-full md:max-w-lg z-50 flex flex-col rounded-2xl overflow-hidden shadow-2xl"
            style={{ backgroundColor: menuBg }}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 shrink-0" style={{ borderBottom: `1px solid ${menuText}10` }}>
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" style={{ color: menuAccent }} />
                <h3 className="text-lg font-bold" style={{ color: menuText }}>
                  Avaliações de Clientes
                </h3>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full flex items-center justify-center transition-all active:scale-90"
                style={{ backgroundColor: `${menuText}08`, color: `${menuText}60` }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 rounded-full animate-spin" style={{ borderColor: `${menuText}20`, borderTopColor: menuAccent }} />
                </div>
              ) : reviews.length === 0 ? (
                <div className="text-center py-12">
                  <Star className="h-10 w-10 mx-auto mb-3" style={{ color: `${menuText}20` }} />
                  <p className="text-sm" style={{ color: `${menuText}50` }}>Nenhuma avaliação ainda</p>
                </div>
              ) : (
                <>
                  {/* Summary */}
                  <div className="flex items-center gap-4 mb-5 pb-4" style={{ borderBottom: `1px solid ${menuText}10` }}>
                    <div className="text-center">
                      <div className="text-3xl font-bold" style={{ color: menuText }}>{avgRating.toFixed(1)}</div>
                      <StarDisplay rating={Math.round(avgRating)} />
                      <div className="text-xs mt-1" style={{ color: `${menuText}70` }}>{reviews.length} avaliações</div>
                    </div>
                    <div className="flex-1 space-y-1">
                      {ratingCounts.map(({ stars, count }) => (
                        <div key={stars} className="flex items-center gap-2">
                          <span className="text-xs w-3 text-right" style={{ color: `${menuText}70` }}>{stars}</span>
                          <Star className="h-3 w-3 fill-current" style={{ color: "hsl(45, 93%, 47%)" }} />
                          <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ backgroundColor: `${menuText}10` }}>
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${reviews.length > 0 ? (count / reviews.length) * 100 : 0}%`,
                                backgroundColor: menuAccent,
                              }}
                            />
                          </div>
                          <span className="text-xs w-6" style={{ color: `${menuText}50` }}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1">
                    {[null, 5, 4, 3].map(f => (
                      <button
                        key={f ?? "all"}
                        onClick={() => { setFilter(f); setShowAll(false); }}
                        className="px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all"
                        style={{
                          backgroundColor: filter === f ? menuAccent : `${menuText}08`,
                          color: filter === f ? "#fff" : `${menuText}80`,
                        }}
                      >
                        {f === null ? "Todas" : `${f}★`}
                      </button>
                    ))}
                  </div>

                  {/* Reviews list */}
                  <div className="space-y-3">
                    {displayed.map(review => {
                      const avg = Math.round((review.food_rating + review.service_rating) / 2);
                      return (
                        <div
                          key={review.id}
                          className="rounded-xl p-3 border"
                          style={{ borderColor: `${menuText}08`, backgroundColor: `${menuText}03` }}
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <div
                                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                                style={{ backgroundColor: `${menuAccent}20`, color: menuAccent }}
                              >
                                {(review.customer_name || "C").charAt(0).toUpperCase()}
                              </div>
                              <span className="text-sm font-semibold" style={{ color: menuText }}>
                                {anonymizeName(review.customer_name)}
                              </span>
                            </div>
                            <span className="text-[10px]" style={{ color: `${menuText}50` }}>
                              {formatDistanceToNow(new Date(review.created_at), { addSuffix: true, locale: ptBR })}
                            </span>
                          </div>
                          <StarDisplay rating={avg} size={12} />
                          {review.tags && review.tags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {review.tags.map((tag, i) => (
                                <span
                                  key={i}
                                  className="px-2 py-0.5 rounded-full text-[10px] font-medium"
                                  style={{ backgroundColor: `${menuAccent}15`, color: menuAccent }}
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                          {review.comment && (
                            <p className="text-xs mt-2 leading-relaxed" style={{ color: `${menuText}80` }}>
                              "{review.comment}"
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {filtered.length > 8 && !showAll && (
                    <button
                      onClick={() => setShowAll(true)}
                      className="flex items-center gap-1 mx-auto mt-4 px-4 py-2 rounded-xl text-sm font-medium transition-all active:scale-95"
                      style={{ color: menuAccent }}
                    >
                      Ver mais avaliações <ChevronDown className="h-4 w-4" />
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
